import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header/Header";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';

// Configuration automatique de l'API
// Utilise HTTPS en production pour éviter les problèmes de Mixed Content
const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api'
  : `${window.location.protocol}//${window.location.hostname}/api`;

export default function Composition() {
  const navigate = useNavigate();
  const location = useLocation();
  const [matchData, setMatchData] = useState(null);
  const [players, setPlayers] = useState([]);
  const [formation, setFormation] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState({});
  const [compo, setCompo] = useState([]);
  const [compositionDetails, setCompositionDetails] = useState([]);
  const [selectedCompositionId, setSelectedCompositionId] = useState(null);

  useEffect(() => {
    // Changer le titre de la page
    document.title = 'ESMANAGER - COMPOSITION';
    
    fetch(`${API_URL}/compo-name`)
      .then(res => res.json())
      .then(data => {
        console.log("Compositions chargées:", data);
        setCompo(data);
        // Sélectionner la première formation par défaut
        if (data.length > 0) {
          setFormation(data[0].label);
          setSelectedCompositionId(data[0].rowid);
        }
      })
      .catch(err => console.error("Erreur chargement compositions:", err));
  }, []);

  // Charger les détails de la composition sélectionnée
  useEffect(() => {
    if (selectedCompositionId) {
      fetch(`${API_URL}/composition/${selectedCompositionId}`)
        .then(res => res.json())
        .then(data => {
          console.log("Détails de la composition:", data);
          setCompositionDetails(data);
        })
        .catch(err => console.error("Erreur chargement détails composition:", err));
    }
  }, [selectedCompositionId]);

  useEffect(() => {
    // Récupérer les données du match depuis la location state ou depuis l'API
    if (location.state?.matchData) {
      setMatchData(location.state.matchData);
    }
    
    // Charger les détails des joueurs sélectionnés
    if (location.state?.selectedPlayers) {
      fetch(`${API_URL}/players`)
        .then(res => res.json())
        .then(data => {
          const selectedPlayersData = data.filter(player =>
            location.state.selectedPlayers.includes(player.rowid)
          );
          setPlayers(selectedPlayersData);
        })
        .catch(err => console.error("Erreur chargement joueurs:", err));
    }
  }, [location.state]);

  const handlePlayerSelect = (position, playerId) => {
    setSelectedPlayers(prev => ({
      ...prev,
      [position]: playerId
    }));
  };

  const handleLaunch = async () => {
    if (!matchData || !players.length) {
      alert("Données manquantes pour sauvegarder");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/matches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          adversaire: matchData.adversaire,
          matchType: matchData.matchType,
          lieu: matchData.lieu,
          selectedPlayers: matchData.selectedPlayers,
          formation: formation,
          composition: selectedPlayers,
          compositionId: selectedCompositionId
        })
      });

      const result = await response.json();
      
      if (result.success) {
        // Rediriger directement vers la page de match en passant l'ID du match
        navigate("/match", { 
          state: { 
            matchId: result.matchId,
            matchData: matchData,
            composition: selectedPlayers,
            players: players,
            fresh: true
          } 
        });
      } else {
        alert("Erreur lors de la sauvegarde: " + result.error);
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la sauvegarde");
    }
  };

  // Générer les positions dynamiquement selon la composition
  const generatePositions = () => {
    const positions = [];
    let positionIndex = 0;

    compositionDetails.forEach((detail, index) => {
      const { nbPlayer_poste, poste_label } = detail;
      
      for (let i = 0; i < nbPlayer_poste; i++) {
        let x, y;
        
        // Positionnement selon le type de poste (basé sur l'image 4-4-2)
        switch (poste_label.toLowerCase()) {
          case 'attaquant':
            // Attaquants : répartition horizontale centrée sur la ligne d'attaque
            if (nbPlayer_poste === 1) {
              x = 50;
              y = 10;
            } else if (nbPlayer_poste === 2) {
              x = 30 + (i * 40);
              y = 10;
            } else if (nbPlayer_poste === 3) {
              if (i === 1) {
                x = 50;
                y = 5;
              } else {
                x = 20 + (i * 30);
                y = 15;
              }
            } else if (nbPlayer_poste === 4) {
              if (i < 2) {
                x = 15 + (i * 70);
                y = 5;
              } else {
                x = 30 + ((i - 2) * 40);
                y = 15;
              }
            } else {
              x = 50;
              y = 10;
            }
            break;
          case 'milieu':
            // Milieux : répartition horizontale centrée sur la ligne de milieu
            if (nbPlayer_poste === 1) {
              x = 50;
              y = 35;
            } else if (nbPlayer_poste === 2) {
              x = 30 + (i * 40);
              y = 35;
            } else if (nbPlayer_poste === 3) {
              if (i === 1) {
                x = 50;
                y = 40;
              } else {
                x = 20 + (i * 30);
                y = 30;
              }
            } else if (nbPlayer_poste === 4) {
              if (i < 2) {
                x = 15 + (i * 70);
                y = 30;
              } else {
                x = 30 + ((i - 2) * 40);
                y = 40;
              }
            } else if (nbPlayer_poste === 5) {
              if (i < 3) {
                x = 15 + (i * 35);
                y = 30;
              } else {
                x = 30 + ((i - 3) * 40);
                y = 45;
              }
            } else {
              x = 50;
              y = 35;
            }
            break;
          case 'défenseur':
            // Défenseurs : répartition horizontale centrée sur la ligne de défense
            if (nbPlayer_poste === 1) {
              x = 50;
              y = 65;
            } else if (nbPlayer_poste === 2) {
              x = 30 + (i * 40);
              y = 65;
            } else if (nbPlayer_poste === 3) {
              if (i === 1) {
                x = 50;
                y = 70;
              } else {
                x = 20 + (i * 30);
                y = 60;
              }
            } else if (nbPlayer_poste === 4) {
              if (i < 2) {
                x = 15 + (i * 70);
                y = 60;
              } else {
                x = 30 + ((i - 2) * 40);
                y = 70;
              }
            } else if (nbPlayer_poste === 5) {
              if (i < 2) {
                x = 15 + (i * 70);
                y = 60;
              } else {
                x = 20 + ((i - 2) * 30);
                y = 70;
              }
            } else {
              x = 50;
              y = 65;
            }
            break;
          case 'gardien':
            // Gardien : toujours au centre en bas
            x = 50;
            y = 85;
            break;
          default:
            x = 50;
            y = 50;
        }
        
        positions.push({
          id: `${poste_label.toLowerCase()}_${i + 1}`,
          label: poste_label.toUpperCase().substring(0, 3),
          x: x,
          y: y,
          poste: poste_label
        });
        
        positionIndex++;
      }
    });
    
    return positions;
  };

  const currentFormation = { positions: generatePositions() };

  // Vérifier si tous les postes sont remplis
  const isFormationComplete = () => {
    if (currentFormation.positions.length === 0) return false;
    
    return currentFormation.positions.every(position => {
      return selectedPlayers[position.id] && selectedPlayers[position.id] !== '';
    });
  };

  return (
    <div>
      <Header />
      <h1>COMPOSITION</h1>
      
      <Box maxWidth={800} mx="auto" px={2}>
        {/* Sélecteur de formation */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            <FormControl variant="standard" sx={{ minWidth: 120 }}>
              <Select
                value={formation}
                onChange={(e) => {
                  const selectedComp = compo.find(comp => comp.label === e.target.value);
                  setFormation(e.target.value);
                  setSelectedCompositionId(selectedComp?.rowid);
                }}
                sx={{
                  color: '#211b3f',
                  '&:before': { borderBottomColor: '#211b3f' },
                  '&:after': { borderBottomColor: '#211b3f' },
                  '& .MuiSelect-icon': { color: '#211b3f' }
                }}
              >
               {compo.map((comp) => (
                 <MenuItem key={comp.rowid} value={comp.label}>{comp.label}</MenuItem>
               ))}
            </Select>
          </FormControl>
        </Box>

        {/* Terrain de football */}
        <Box 
          sx={{ 
            position: 'relative',
            width: '100%',
            height: '400px',
            backgroundColor: '#2e7d32',
            borderRadius: 3,
            border: '3px solid #fff',
            mb: 4,
            overflow: 'hidden'
          }}
        >
          {/* Lignes du terrain */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            height: '2px',
            backgroundColor: '#fff',
            transform: 'translateY(-50%)'
          }} />
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '20px',
            height: '20px',
            backgroundColor: '#fff',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)'
          }} />
          
          {/* Positions des joueurs */}
          {currentFormation.positions.map((position) => (
            <Box
              key={position.id}
              sx={{
                position: 'absolute',
                left: `${position.x}%`,
                top: `${position.y}%`,
                transform: 'translate(-50%, -50%)',
                minWidth: '80px'
              }}
            >
              <FormControl variant="standard" size="small">
                <Select
                  value={selectedPlayers[position.id] || ''}
                  onChange={(e) => handlePlayerSelect(position.id, e.target.value)}
                  displayEmpty
                  sx={{
                    color: '#fff',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderRadius: 1,
                    minWidth: '80px',
                    '&:before': { borderBottomColor: 'transparent' },
                    '&:after': { borderBottomColor: 'transparent' },
                    '& .MuiSelect-select': { 
                      padding: '4px 8px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      textAlign: 'center'
                    },
                    '& .MuiSelect-icon': { color: '#fff' }
                  }}
                >
                  <MenuItem value="">
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                      {position.label}
                    </Typography>
                  </MenuItem>
                  {(() => {
                    const selectedIds = Object.values(selectedPlayers).filter(Boolean);
                    const currentSelectedId = selectedPlayers[position.id];
                    const availablePlayers = players.filter((player) => {
                      // Garder visible le joueur déjà sélectionné pour ce poste
                      if (player.rowid === currentSelectedId) return true;
                      // Exclure les joueurs déjà sélectionnés à d'autres postes
                      return !selectedIds.includes(player.rowid);
                    });
                    return availablePlayers.map((player) => (
                      <MenuItem key={player.rowid} value={player.rowid}>
                        <Typography variant="caption">
                          {player.firstname}. {player.lastname?.charAt(0)}
                        </Typography>
                      </MenuItem>
                    ));
                  })()}
                </Select>
              </FormControl>
            </Box>
          ))}
        </Box>

        {/* Boutons d'action */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center' }}>
          <Button 
            variant="contained"
            onClick={handleLaunch}
            disabled={!isFormationComplete()}
            sx={{
              backgroundColor: isFormationComplete() ? '#1976d2' : '#ccc',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: isFormationComplete() ? '#1565c0' : '#ccc',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
                color: '#666',
                cursor: 'not-allowed'
              }
            }}
          >
            LANCER
          </Button>
          <Typography 
            variant="body2" 
            onClick={() => navigate(-1)}
            sx={{ 
              cursor: 'pointer',
              color: '#666',
              textAlign: 'center',
              textDecoration: 'underline',
              '&:hover': { color: 'black' }
            }}
          >
            Annuler
          </Typography>
        </Box>
      </Box>
    </div>
  );
}
